if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // Display the selected configuration synchronously. Price, stock and
        // purchase inputs remain disabled until Shopify confirms the selection.
        if (!shouldSwapProduct) this.showLocalConfiguration(selectedOptionValues);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.updateVariantContent(null, null, true);
        this.updateConfigurationGalleryBadge(null, null);
        this.querySelector('[data-variant-retry]')?.remove();
        this.localConfigurationMarkup = {};
        this.localPreorderMarkup = {};
        for (const selector of ['[data-configuration-summary]', '[data-configuration-content]']) {
          const configuration = this.querySelector(selector);
          if (configuration) {
            configuration.innerHTML = '';
            configuration.setAttribute('aria-busy', 'true');
          }
        }
        this.clearPreorderContent();
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else if(this.dataset.updateVariantsOnly === 'true') {
            this.querySelector('.product-form-wrapper .product-form .product-variant-id').value = html.querySelector('.product-form-wrapper .product-form .product-variant-id').value;
            this.updatePreorderContent(html.querySelector(selector), variant);
            if(html.querySelector('.product-form-wrapper .product-form button[type="submit"]').hasAttribute('disabled')) {
              this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
            } else {
              const label = html.querySelector('.product-form-wrapper .product-form button[type="submit"] span.text')?.textContent.trim();
              this.productForm?.toggleSubmitButton(false, label || 'ADD');
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        return fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (controller.signal.aborted || !this.isConnected) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!html.querySelector('product-info')) throw new Error('Product response is incomplete');
            this.pendingRequestUrl = null;
            callback(html);
            document.getElementById(targetId)?.focus();
          })
          .catch((error) => {
            if (controller.signal.aborted || !this.isConnected) return;
            this.pendingRequestUrl = null;
            this.productForm?.toggleSubmitButton(true);
            this.productForm?.handleErrorMessage(window.cartStrings.error);
            this.querySelector('[data-variant-retry]')?.remove();
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'pd-button pd-button--light';
            retry.dataset.variantRetry = '';
            retry.textContent = 'Retry selection';
            retry.addEventListener('click', () => {
              this.resetProductFormState();
              this.renderProductInfo({ requestUrl, targetId, callback });
            }, { once: true });
            this.productForm?.appendChild(retry);
            console.error(error);
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && this.sectionId && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateConfigurationContent(html, variant);
          this.updatePreorderContent(html, variant);
          this.updateVariantContent(html, variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('StickyPrice');
          updateSourceFromDestination('PurchasePrice', (source) => source.classList.contains('hidden'));
          updateSourceFromDestination('StickyConfiguration');
          updateSourceFromDestination('Scarcity');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}-${this.dataset.productId}`)
            || html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = sourceButton?.hasAttribute('disabled') ?? true;
          const buttonLabel = sourceButton?.querySelector('span.text')?.textContent.trim();
          this.productForm?.toggleSubmitButton(isDisabled, buttonLabel || (isDisabled ? window.variantStrings.soldOut : window.variantStrings.addToCart));

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      showLocalConfiguration(selectedOptionValues) {
        const catalogue = this.querySelector('[data-configuration-variants]');
        const destination = this.querySelector('[data-configuration-content]');
        if (!catalogue || !destination || catalogue.dataset.productId !== this.dataset.productId) return false;
        if (!selectedOptionValues.length || selectedOptionValues.some((id) => !id)) return false;
        const selected = selectedOptionValues.map(String);
        const matches = Array.from(catalogue.querySelectorAll('template[data-configuration-options]')).filter((template) => {
          const ids = template.dataset.configurationOptions.split(',');
          return ids.every(Boolean) && selected.every((id) => ids.includes(id));
        });
        // Hidden legacy options can leave multiple possible variants. Do not guess.
        if (matches.length !== 1) return false;
        const template = matches[0];
        const source = template.content.querySelector('[data-configuration-content]');
        if (!source) return false;
        this.localConfigurationMarkup = {};
        for (const selector of ['[data-configuration-summary]', '[data-configuration-content]']) {
          const target = this.querySelector(selector);
          const fragment = template.content.querySelector(selector);
          if (!target) continue;
          target.innerHTML = fragment?.innerHTML || '';
          this.localConfigurationMarkup[selector] = target.innerHTML;
          target.dataset.variantId = template.dataset.variantId;
          target.removeAttribute('aria-busy');
        }
        this.updateConfigurationGalleryBadge(template.content, template.dataset.variantId);
        this.updateVariantContent(template.content, template.dataset.variantId);
        // Display only: the purchase button and order properties still wait for
        // Shopify. Do not remove/reinsert these rows after a network round trip.
        this.localPreorderMarkup = {};
        for (const selector of ['[data-preorder-content]', '[data-preorder-main-dispatch]', '[data-preorder-sticky-dispatch]', '[data-preorder-legacy-timeline]', '[data-preorder-legacy-message]']) {
          const target = this.querySelector(selector);
          if (!target) continue;
          const fragment = template.content.querySelector(selector);
          target.innerHTML = fragment?.innerHTML || '';
          this.localPreorderMarkup[selector] = target.innerHTML;
          target.dataset.variantId = template.dataset.variantId;
          target.removeAttribute('aria-busy');
        }
        return true;
      }

      updateVariantContent(source, variantId, pending = false) {
        const catalogue = this.querySelector('[data-configuration-variants]');
        const cached = variantId && catalogue?.dataset.productId === this.dataset?.productId
          ? Array.from(catalogue.querySelectorAll('template[data-variant-id]')).find(template => template.dataset.variantId === String(variantId)) : null;
        for (const name of ['features', 'features-remaining', 'key-specs', 'faq', 'metadata']) {
          const selector = `[data-variant-content="${name}"]`;
          const destination = this.querySelector(selector);
          if (!destination) continue;
          const content = variantId ? source?.querySelector(selector)?.innerHTML || '' : '';
          if (destination.innerHTML !== content) destination.innerHTML = content;
          destination.dataset.variantId = variantId || '';
          if (pending) destination.setAttribute('aria-busy', 'true');
          else destination.removeAttribute('aria-busy');
          const cachedContent = cached?.content.querySelector(selector);
          if (cachedContent && cachedContent.innerHTML !== content) cachedContent.innerHTML = content;
        }
        const overview = this.querySelector('.fr-product-overview');
        this.updateRidePresentation(variantId);
        if (overview) overview.hidden = !overview.querySelector('.pd-main-product__short-description-wrapper, .pd-main-product__key-specs-wrapper');
        const status = this.querySelector('[data-variant-content="metadata"] [data-en15194-status]')?.dataset.en15194Status || '';
        document.querySelectorAll('[data-compliance-product-id]').forEach(section => {
          if (section.dataset.complianceProductId !== this.dataset?.productId) return;
          section.querySelectorAll('[data-compliance-status]').forEach(badge => { badge.hidden = !variantId || badge.dataset.complianceStatus !== status; });
        });
      }

      updateConfigurationGalleryBadge(source, variantId) {
        const badge = variantId && source?.querySelector?.('[data-configuration-gallery-badge]');
        for (const gallery of ['.pd-main-product__slider', '.pd-main-product__image-wrapper']) {
          const destination = this.querySelector(`${gallery} [data-configuration-gallery-badge]`);
          if (!destination) continue;
          destination.innerHTML = badge?.innerHTML || '';
          destination.dataset.variantId = variantId || '';
        }
      }

      updateRidePresentation(variantId) {
        if (!variantId) return;
        const metadata = this.querySelector('[data-variant-content="metadata"]');
        const model = metadata?.querySelector('[data-ride-model]');
        if (!model) return;
        this.querySelectorAll('[data-ride-model], [data-ride-title]').forEach(label => {
          if (metadata.contains(label)) return;
          label.textContent = label.hasAttribute('data-ride-model') ? model.dataset.rideModel : model.dataset.rideTitle;
        });
        const details = metadata.querySelector('template[data-ride-details]');
        if (!details || this.dataset.originalSection) return;
        document.querySelectorAll('[data-ride-details-product]').forEach(body => {
          if (body.dataset.rideDetailsProduct !== this.dataset.productId) return;
          if (body.innerHTML !== details.innerHTML) body.innerHTML = details.innerHTML;
          body.dataset.variantId = String(variantId);
        });
      }

      updateConfigurationContent(html, variant) {
        this.updateConfigurationGalleryBadge(html, variant?.id);
        const catalogue = this.querySelector('[data-configuration-variants]');
        const cached = variant && catalogue && catalogue.dataset.productId === this.dataset.productId
          ? Array.from(catalogue.querySelectorAll('template[data-variant-id]'))
            .find((template) => template.dataset.variantId === String(variant.id)) : null;
        for (const [selector, prefix] of [['[data-configuration-summary]', 'ConfigurationSummary'], ['[data-configuration-content]', 'Configuration']]) {
          const destination = this.querySelector(selector);
          if (!destination) continue;
          const source = html.getElementById(`${prefix}-${this.sectionId}`);
          const content = variant && source ? source.innerHTML : '';
          const sameLocalContent = destination.dataset.variantId === String(variant?.id ?? '') && this.localConfigurationMarkup?.[selector] === content;
          // Preserve an open information dialog when the server confirms it.
          if (!sameLocalContent && destination.innerHTML !== content) destination.innerHTML = content;
          destination.dataset.variantId = variant?.id ?? '';
          destination.removeAttribute('aria-busy');
          // Server corrections must also replace the local display copy.
          const cachedContent = cached?.content.querySelector(selector);
          if (cachedContent) cachedContent.innerHTML = content;
        }
        this.localConfigurationMarkup = {};
      }

      clearPreorderContent() {
        for (const selector of ['[data-preorder-content]', '[data-preorder-properties]', '[data-preorder-main-dispatch]', '[data-preorder-sticky-dispatch]', '[data-preorder-legacy-timeline]', '[data-preorder-legacy-message]']) {
          const destination = this.querySelector(selector);
          if (destination) {
            destination.innerHTML = '';
            destination.setAttribute('aria-busy', 'true');
          }
        }
      }

      updatePreorderContent(html, variant) {
        const catalogue = this.querySelector('[data-configuration-variants]');
        const cached = variant && catalogue && catalogue.dataset.productId === this.dataset?.productId
          ? Array.from(catalogue.querySelectorAll('template[data-variant-id]'))
            .find((template) => template.dataset.variantId === String(variant.id)) : null;
        for (const selector of ['[data-preorder-content]', '[data-preorder-properties]', '[data-preorder-main-dispatch]', '[data-preorder-sticky-dispatch]', '[data-preorder-legacy-timeline]', '[data-preorder-legacy-message]']) {
          const destination = this.querySelector(selector);
          if (!destination) continue;
          const source = html.querySelector(selector);
          const content = variant && source ? source.innerHTML : '';
          const sameLocalContent = destination.dataset.variantId === String(variant?.id ?? '') && this.localPreorderMarkup?.[selector] === content;
          if (!sameLocalContent && destination.innerHTML !== content) destination.innerHTML = content;
          destination.dataset.variantId = variant?.id ?? '';
          destination.removeAttribute('aria-busy');
          const cachedContent = cached?.content.querySelector(selector);
          if (cachedContent) cachedContent.innerHTML = content;
        }
        this.localPreorderMarkup = {};
      }

      updateVariantInputs(variantId) {
        const selectedVariantQuantity = this.querySelector('variant-selects')?.dataset?.selectedVariantQuantity;
        if (selectedVariantQuantity <= 5 && selectedVariantQuantity > 0) {
          this.querySelector('.pd-main-product__quantity-warning')?.classList?.remove('hidden');
          if(this.querySelector('.pd-main-product__quantity-warning__text')) {
            this.querySelector('.pd-main-product__quantity-warning__text').innerHTML = `Only ${selectedVariantQuantity} left in stock`;
          }
        } else {
          this.querySelector('.pd-main-product__quantity-warning')?.classList?.add('hidden');
          if(this.querySelector('.pd-main-product__quantity-warning__text')) {
            this.querySelector('.pd-main-product__quantity-warning__text').innerHTML = '';
          }
        }
        this.querySelectorAll(
          `#product-form-${this.dataset.section}-${this.dataset.productId}, #product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'StickyPrice', 'PurchasePrice', 'Scarcity', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const customGallery = this.querySelector('.pd-main-product__slider swiper-carousel');
        if (customGallery) {
          const slides = Array.from(customGallery.querySelectorAll('[data-product-media-id]'));
          const index = slides.findIndex((slide) => slide.dataset.productMediaId === String(variantFeaturedMediaId));
          if (index >= 0) {
            // Keep the latest variant image ready while the mobile gallery is hidden.
            customGallery.setAttribute('data-initial-slide', String(index));
            customGallery.swiper?.slideTo(index);
          }
        }
        const desktopImage = this.querySelector(`#ProductImage-${this.dataset.section}-${variantFeaturedMediaId}`);
        if (desktopImage) {
          desktopImage.loading = 'eager';
          desktopImage.parentElement.prepend(desktopImage);
        }
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
